import React, { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { confirmSubscriptionPayment } from "api";

// Import your custom alerts
import SuccessAlert from "components/alerts/SuccessAlerts";
import ErrorAlerts from "components/alerts/ErrorAlerts";

const PaymentConfirmation = ({ isOpen, onClose, subscriptionId, paymentInfo = {} }) => {
  const [referenceNumber, setReferenceNumber] = useState("");
  const queryClient = useQueryClient();

  // Local states for success/error messages
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessages, setErrorMessages] = useState([]);

  // React Query mutation
  const {
    mutate: confirmPayment,
    isLoading,
    isError,
    error,
  } = useMutation({
    mutationFn: () => confirmSubscriptionPayment(subscriptionId, referenceNumber),
    onSuccess: (data) => {
      setSuccessMessage("M-Pesa payment confirmed successfully!");
      setErrorMessages([]);
      
      // Invalidate and refetch the course statuses to update the UI
      queryClient.invalidateQueries(['courseStatuses']);
      queryClient.invalidateQueries(['courseSubscriptions']);
      
      // Also invalidate the specific user's course statuses
      if (paymentInfo && paymentInfo.userId) {
        queryClient.invalidateQueries(['courseStatuses', paymentInfo.userId]);
        // Force refetch the specific user's course statuses
        queryClient.refetchQueries(['courseStatuses', paymentInfo.userId]);
      }
      
      // Force refetch all course statuses
      queryClient.refetchQueries(['courseStatuses']);
      
      // Clear any cached data to ensure fresh fetch
      queryClient.removeQueries(['courseStatuses']);
      
      // Close modal after 2 seconds
      setTimeout(() => {
        onClose();
      }, 2000);
    },
    onError: (err) => {
      // If you receive a server message, handle it accordingly
      const errorMsg = err?.response?.data?.message || err.message || "Error confirming payment.";
      setErrorMessages([errorMsg]);
      setSuccessMessage("");
    },
  });

  // When user clicks "Confirm Payment"
  const handleConfirm = () => {
    if (referenceNumber.trim()) {
      confirmPayment();
    } else {
      // If you want to show an ErrorAlert here too, you can:
      setErrorMessages(["Please enter the M-Pesa reference number"]);
    }
  };

  // Clear reference number when modal opens
  useEffect(() => {
    if (isOpen) {
      setReferenceNumber(""); // Don't pre-fill for manual M-Pesa confirmation
      setErrorMessages([]);
      setSuccessMessage("");
    }
  }, [isOpen]);

  // If not open, render nothing
  if (!isOpen) return null;

  // Dismiss success alert
  const handleCloseSuccess = () => {
    setSuccessMessage("");
    // Optionally close the modal too
    // onClose();
  };

  // Dismiss error alert(s)
  const handleCloseError = () => {
    setErrorMessages([]);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Modal Container */}
      <div className="w-96 rounded-lg bg-white p-6 shadow-lg">
        {/* Success Alert */}
        {successMessage && (
          <SuccessAlert message={successMessage} onClose={handleCloseSuccess} />
        )}

        {/* Error Alerts */}
        {errorMessages.length > 0 && (
          <ErrorAlerts errors={errorMessages} onClose={handleCloseError} />
        )}

        <h2 className="mb-4 text-xl font-bold">Confirm M-Pesa Payment</h2>

        {/* Payment Information */}
        {paymentInfo && Object.keys(paymentInfo).length > 0 && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Payment Details:</h3>
            <div className="text-sm text-gray-600 space-y-1">
              <div><strong>Amount:</strong> {paymentInfo.amount}</div>
              <div><strong>Phone:</strong> {paymentInfo.userPhone}</div>
              <div><strong>System Reference:</strong> {paymentInfo.accountReference}</div>
              {paymentInfo.transactionId && paymentInfo.transactionId !== 'N/A' && (
                <div><strong>Transaction ID:</strong> {paymentInfo.transactionId}</div>
              )}
            </div>
          </div>
        )}

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            M-Pesa Reference Number (Receipt Number)
          </label>
          <input
            type="text"
            placeholder="Enter the M-Pesa receipt number from student"
            value={referenceNumber}
            onChange={(e) => setReferenceNumber(e.target.value)}
            className="w-full rounded border border-gray-300 p-2 focus:border-brand-500 focus:outline-none"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter the M-Pesa reference number that the student received after making the payment
          </p>
        </div>

        {/* If using isError from React Query and not handled above, you can show something else:
            {isError && !errorMessages.length && (
              <p className="mb-2 text-red-500">Error: {error.message}</p>
            )}
        */}

        <button
          onClick={handleConfirm}
          disabled={isLoading}
          className="w-full rounded bg-orange-500 p-2 text-white transition-colors duration-200 hover:bg-brand-900 disabled:bg-gray-400"
        >
          {isLoading ? "Confirming..." : "Confirm M-Pesa Payment"}
        </button>

        <button
          onClick={onClose}
          className="mt-2 w-full text-center text-dark-500"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default PaymentConfirmation;
